import React from 'react';
import ApiValidationErrorDisplay from '~/components/ApiValidationErrorDisplay';
import { clearApiValidationErrors } from '~/stores/redux/common/apiValidationErrorSlice';
import { schema } from './consumable.yup';
import DisplayFormikState from '~/components/DisplayFormikState';
import SubmitResetButtons from '~/components/FormControl/SubmitResetButtons';
import classNames from 'classnames/bind';
const cx = classNames.bind();
import { FormText, FormSelect, TextArea, FormNumber, Switch } from '~/components/FormControl';
import { Formik } from 'formik';
import { Card, Form, Col } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { checkDuplicateName } from '~/utils/validators.yup';
import { API_PREFIX } from './consumable.api';
import ConsumableEffectTable from './ConsumableEffectTable';
import { FileUploadedWithResetCount } from '~/components/FileUploaded';

ConsumableForm.defaultProps = {
    pictureAttributeNames: null,
};
ConsumableForm.propTypes = {
    initialValues: PropTypes.object.isRequired,
    setResetCount: PropTypes.func.isRequired,
    pictureAttributeNames: PropTypes.array,
    resetCount: PropTypes.number.isRequired,
    formSubmit: PropTypes.func.isRequired,
};

function ConsumableForm({ initialValues, setResetCount, pictureAttributeNames, resetCount, formSubmit }) {
    const { isLoading } = useSelector((state) => state.loading);
    const { apiValidationErrors } = useSelector((state) => state.apiValidationError);
    const { rarities } = useSelector((state) => state.itemSaga.consumable);

    return (
        <>
            {/* MUST put `isEmpty(heroMetaData)` in condition, else Bootstrap `Select` will not display value to UI (even though it's already stored value in it)  */}
            {!isEmpty(initialValues) ? (
                <Card>
                    <Card.Body>
                        <Formik
                            initialValues={initialValues}
                            validateOnChange={false}
                            validationSchema={initialValues.id === null ? schema : schema(true)}
                            onSubmit={formSubmit}
                        >
                            {(props) => {
                                // eslint-disable-next-line react/prop-types
                                const { handleSubmit, isSubmitting, resetForm, values } = props;

                                const asyncApiCall = checkDuplicateName(initialValues.name, API_PREFIX); // must put inside formik's callback, so that this `checkDuplicateName` will always return new closure every time formik re-renders, which is necessary as I use <FastField> with `validate` prop, which under the hood compares its current props with last props and only re-render if props actually change. But if I put this snippet outside formik's callback, then `asyncApiCall` of the next re-render is just of the original one from the scope above (this is one of closure features) so it will not re-render even though param passed to `asyncApiCall` (`initialValues.name`) has actually changed.

                                return (
                                    <>
                                        <SubmitResetButtons
                                            isSubmitting={isSubmitting}
                                            handleSubmit={handleSubmit}
                                            setResetCount={setResetCount}
                                            resetForm={resetForm}
                                        />

                                        <Form.Row className="align-content-center flex-column">
                                            <Switch name="isRotational" />
                                        </Form.Row>
                                        <Form.Row className="mt-3 justify-content-between">
                                            <FormText
                                                sm={3}
                                                name="name"
                                                asyncApiCall={asyncApiCall}
                                                className="d-flex flex-column justify-content-end"
                                            />
                                            {pictureAttributeNames.map((imgName, i) => (
                                                <Col
                                                    key={i}
                                                    sm={3}
                                                >
                                                    <FileUploadedWithResetCount
                                                        pictureName={imgName}
                                                        pictureVal={values[imgName]}
                                                        resetCount={resetCount}
                                                        id={imgName}
                                                    />
                                                </Col>
                                            ))}
                                            <FormSelect
                                                name="rarity"
                                                options={rarities}
                                                sm={3}
                                                className="d-flex flex-column justify-content-end"
                                            />
                                        </Form.Row>

                                        <Form.Row className="mt-3 justify-content-between">
                                            <TextArea
                                                name="description"
                                                sm={3}
                                                rows={2}
                                            />
                                            <FormNumber
                                                name="value"
                                                sm={3}
                                                className="offset-sm-1"
                                            />
                                            <FormNumber
                                                name="inventoryLimit"
                                                sm={3}
                                                className="offset-sm-1"
                                            />
                                        </Form.Row>

                                        <Form.Row>
                                            <ConsumableEffectTable resetCount={resetCount} />
                                        </Form.Row>
                                        <SubmitResetButtons
                                            isSubmitting={isSubmitting}
                                            handleSubmit={handleSubmit}
                                            setResetCount={setResetCount}
                                            resetForm={resetForm}
                                        />
                                        <ApiValidationErrorDisplay
                                            errors={apiValidationErrors}
                                            clearError={clearApiValidationErrors}
                                        />
                                        <DisplayFormikState props={props} />
                                    </>
                                );
                            }}
                        </Formik>
                    </Card.Body>
                </Card>
            ) : (
                <Card style={{ position: 'relative' }}>
                    <Card.Body className={cx('row', { whirl: isLoading })}></Card.Body>
                </Card>
            )}
        </>
    );
}

export default ConsumableForm;
